import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PriceCurrencyTooltipProps {
  prices: Record<string, number> | null;
  baseCurrency: string;
  className?: string;
}

const ZERO_DECIMAL_CURRENCIES = new Set(['UZS']);

export function formatCurrencyPrice(amount: number, currency: string): string {
  const fractionDigits = ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2;

  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);

  return `${currency} ${formatted}`;
}

export function PriceCurrencyTooltip({
  prices,
  baseCurrency,
  className,
}: PriceCurrencyTooltipProps) {
  if (!prices || Object.keys(prices).length === 0) {
    return <span className={cn('text-muted-foreground', className)}>—</span>;
  }

  const baseAmount = prices[baseCurrency];
  const otherCurrencies = Object.keys(prices).filter((c) => c !== baseCurrency);

  const baseDisplay = baseAmount !== undefined
    ? formatCurrencyPrice(baseAmount, baseCurrency)
    : '—';

  if (otherCurrencies.length === 0) {
    return <span className={className}>{baseDisplay}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('cursor-help border-b border-dotted border-current', className)}>
          {baseDisplay}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div className="flex flex-col gap-0.5">
          <span className="font-bold">{baseDisplay}</span>
          {otherCurrencies.map((currency) => (
            <span key={currency}>
              {formatCurrencyPrice(prices[currency], currency)}
            </span>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
